import { resources } from "@constants/hardcoded-strings";
import {
    AXNUIContent,
    AXNUILayout,
    AXNUITheme
} from "@iddataweb/idw-common-ui/types";
import { TabTitleWithErrorCount } from "@containers/common/tab-title-with-error-count/tab-title-with-error-count";
import { PreviewEntityTypes } from "@enums/preview-entity-types";
import { ThemingValidationErrors } from "@idw-types/common/validation/common/theming-validation-errors";
import { getThemingHeaderValidationErrors } from "./theming-header-validation-util";
import { getThemingFooterValidationErrors } from "./theming-footer-validation-util";
import { getThemingActionButtonValidationErrors } from "./theming-action-button-validation-util";
import { getThemingFormPanelValidationErrors } from "./theming-form-panel-validation-util";
import { getThemingSuccessPageValidationErrors } from "./theming-success-page-validation-util";
import { getThemingErrorPageValidationErrors } from "./theming-error-page-validation-util";
import { getThemingCustomCssValidationErrors } from "./theming-custom-css-validation-util";
import { getThemingFormContentValidationErrors } from "./theming-form-content-validation-util";
import { getThemingGenericContentSlotValidationErrors } from "./theming-generic-content-slot-validation-util";
import { getThemingMessagingValidationErrors } from "./theming-messaging-validation-util";
import { getThemingLandingPageValidationErrors } from "./theming-landing-page-validation-util";
import { getThemingAsyncWaitingPageValidationErrors } from "./theming-async-waiting-page-validation-util";
import { getThemingMobileCompletePageValidationErrors } from "./theming-mobile-complete-page-validation-util";
import { getThemingKbaFormValidationErrors } from "./theming-kba-form-validation-util";
import { FormattedMessagingTemplate } from "@idw-types/data/messaging-template";
import { MessagingTemplateTypes } from "@enums/messaging-template-types";
import { EmailTemplateFields } from "@idw-types/common/email-template-fields";
import { getThemingPolicyRetryMessageValidationErrors } from "@utils/validation/common/theming/theming-policy-retry-message-validation-util";
import { FormattedRpServiceConfiguration } from "@idw-types/data/rp-service-configuration";
import { BaseRedirectUrl } from "@idw-types/redirect-url-information";

export const getThemingValidationErrors = (
    previewEntityType: PreviewEntityTypes,
    isUsingThemedUI: boolean,
    hasDisplayableDocumentCaptureAttributes: boolean,
    showMessagingTab: boolean,
    showIncodeFlowTabs: boolean,
    showKbaFormTab: boolean,
    showEmailInputs: boolean,
    emailTemplateFields: EmailTemplateFields,
    rpServiceConfiguration: FormattedRpServiceConfiguration,
    axnuiContent: AXNUIContent,
    axnuiLayout: AXNUILayout,
    axnuiTheme: AXNUITheme,
    isDisplayResultsEnabled: boolean,
    redirectUrls: Array<BaseRedirectUrl>,
    availableMessagingTemplateTypes: Array<MessagingTemplateTypes>,
    selectedMessagingTemplates: Array<FormattedMessagingTemplate>
): ThemingValidationErrors => {
    const headerValidationErrors = getThemingHeaderValidationErrors(
        isUsingThemedUI,
        axnuiContent,
        axnuiLayout,
        axnuiTheme
    );
    const footerValidationErrors = getThemingFooterValidationErrors(
        isUsingThemedUI,
        axnuiContent,
        axnuiLayout,
        axnuiTheme
    );
    const actionButtonValidationErrors = getThemingActionButtonValidationErrors(
        isUsingThemedUI,
        hasDisplayableDocumentCaptureAttributes,
        axnuiContent,
        axnuiTheme
    );
    const formPanelValidationErrors = getThemingFormPanelValidationErrors(
        isUsingThemedUI,
        axnuiContent,
        axnuiLayout
    );
    const preFormContentSlotValidationErrors = getThemingGenericContentSlotValidationErrors(
        previewEntityType,
        isUsingThemedUI,
        resources.pages.verificationServiceDetails.tabs.theming.subTabs.preFormContentSlot.tabName,
        axnuiContent.postFormHelpText,
        axnuiLayout.helpText?.preformHelpText
    );
    const formContentValidationErrors = getThemingFormContentValidationErrors(
        previewEntityType,
        isUsingThemedUI,
        hasDisplayableDocumentCaptureAttributes,
        axnuiContent
    );
    const postFormContentSlotValidationErrors = getThemingGenericContentSlotValidationErrors(
        previewEntityType,
        isUsingThemedUI,
        resources.pages.verificationServiceDetails.tabs.theming.subTabs.postFormContentSlot.tabName,
        axnuiContent.postFormHelpText,
        axnuiLayout.helpText?.postFormHelpText
    );
    const postSubmitContentSlotValidationErrors = getThemingGenericContentSlotValidationErrors(
        previewEntityType,
        isUsingThemedUI,
        resources.pages.verificationServiceDetails.tabs.theming.subTabs.postSubmitContentSlot.tabName,
        axnuiContent.postFormHelpText,
        axnuiLayout.helpText?.postSubmitButtonFormHelpText
    );
    const policyRetryMessageValidationErrors = getThemingPolicyRetryMessageValidationErrors(
        isUsingThemedUI,
        axnuiTheme,
        axnuiContent,
        rpServiceConfiguration
    );

    const successPageValidationErrors = getThemingSuccessPageValidationErrors(
        isUsingThemedUI,
        axnuiContent,
        axnuiLayout
    );
    const errorPageValidationErrors = getThemingErrorPageValidationErrors(
        isUsingThemedUI,
        axnuiContent
    );
    const customCssValidationErrors = getThemingCustomCssValidationErrors(
        previewEntityType,
        isUsingThemedUI,
        axnuiTheme
    );
    const messagingValidationErrors = getThemingMessagingValidationErrors(
        previewEntityType,
        isUsingThemedUI,
        showMessagingTab,
        showEmailInputs,
        emailTemplateFields,
        availableMessagingTemplateTypes,
        selectedMessagingTemplates
    );

    const landingPageValidationErrors = getThemingLandingPageValidationErrors(
        isUsingThemedUI,
        isDisplayResultsEnabled,
        redirectUrls,
        axnuiContent,
        axnuiLayout
    );

    const asyncWaitingPageValidationErrors = getThemingAsyncWaitingPageValidationErrors(
        isUsingThemedUI,
        showIncodeFlowTabs,
        axnuiContent
    );

    const mobileCompletePageValidationErrors = getThemingMobileCompletePageValidationErrors(
        isUsingThemedUI,
        showIncodeFlowTabs,
        axnuiContent
    );

    const kbaFormValidationErrors = getThemingKbaFormValidationErrors(
        isUsingThemedUI,
        showKbaFormTab,
        axnuiContent
    );

    const count = headerValidationErrors.count
        + footerValidationErrors.count
        + actionButtonValidationErrors.count
        + formPanelValidationErrors.count
        + preFormContentSlotValidationErrors.count
        + formContentValidationErrors.count
        + postFormContentSlotValidationErrors.count
        + postSubmitContentSlotValidationErrors.count
        + successPageValidationErrors.count
        + errorPageValidationErrors.count
        + customCssValidationErrors.count
        + messagingValidationErrors.count
        + landingPageValidationErrors.count
        + policyRetryMessageValidationErrors.count
        + asyncWaitingPageValidationErrors.count
        + mobileCompletePageValidationErrors.count
        + kbaFormValidationErrors.count;

    return {
        header: headerValidationErrors,
        footer: footerValidationErrors,
        actionButton: actionButtonValidationErrors,
        preFormContentSlot: preFormContentSlotValidationErrors,
        formPanel: formPanelValidationErrors,
        postFormContentSlot: postFormContentSlotValidationErrors,
        postSubmitContentSlot: postSubmitContentSlotValidationErrors,
        formContent: formContentValidationErrors,
        successPage: successPageValidationErrors,
        errorPage: errorPageValidationErrors,
        customCss: customCssValidationErrors,
        messaging: messagingValidationErrors,
        landingPage: landingPageValidationErrors,
        asyncWaitingPage: asyncWaitingPageValidationErrors,
        mobileCompletePage: mobileCompletePageValidationErrors,
        policyRetryMessage: policyRetryMessageValidationErrors,
        kbaForm: kbaFormValidationErrors,
        count: count,
        tabTitle: <TabTitleWithErrorCount
            tabName={resources.pages.verificationServiceDetails.tabs.theming.tabName}
            errorCount={count}
        />
    }
};
